//
// Pointer file parsing

import flow from 'lodash/flow';
import fromPairs from 'lodash/fromPairs';
import getBlobSHA from './getBlobSHA';
function splitIntoLines(str) {
  return str.split('\n');
}
function splitIntoWords(str) {
  return str.split(/\s+/g);
}
function isNonEmptyString(str) {
  return str !== '';
}
const withoutEmptyLines = flow([lines => lines.map(str => str.trim()), lines => lines.filter(isNonEmptyString)]);
export const parsePointerFile = flow([splitIntoLines, withoutEmptyLines, lines => lines.map(splitIntoWords), fromPairs, ({
  size,
  oid,
  ...rest
}) => ({
  size: parseInt(size),
  sha: oid?.split(':')[1],
  ...rest
})]);

//
// .gitattributes file parsing

function removeGitAttributesCommentsFromLine(line) {
  return line.split('#')[0];
}
function parseGitPatternAttribute(attributeString) {
  // There are three kinds of attribute settings:
  // - a key=val pair sets an attribute to a specific value
  // - a key without a value and a leading hyphen sets an attribute to false
  // - a key without a value and no leading hyphen sets an attribute
  //   to true
  if (attributeString.includes('=')) {
    return attributeString.split('=');
  }
  if (attributeString.startsWith('-')) {
    return [attributeString.slice(1), false];
  }
  return [attributeString, true];
}
const parseGitPatternAttributes = flow([attributes => attributes.map(parseGitPatternAttribute), fromPairs]);
const parseGitAttributesPatternLine = flow([splitIntoWords, ([pattern, ...attributes]) => [pattern, parseGitPatternAttributes(attributes)]]);
const parseGitAttributesFileToPatternAttributePairs = flow([splitIntoLines, lines => lines.map(removeGitAttributesCommentsFromLine), withoutEmptyLines, lines => lines.map(line => parseGitAttributesPatternLine(line))]);
export const getLargeMediaPatternsFromGitAttributesFile = flow([parseGitAttributesFileToPatternAttributePairs, pairs => pairs.filter(([, attributes]) => attributes.filter === 'lfs' && attributes.diff === 'lfs' && attributes.merge === 'lfs'), pairs => pairs.map(([pattern]) => pattern)]);
export function createPointerFile({
  size,
  sha
}) {
  return `\
version https://git-lfs.github.com/spec/v1
oid sha256:${sha}
size ${size}
`;
}
export async function getPointerFileForMediaFileObj(client, fileObj, path) {
  const {
    name,
    size
  } = fileObj;
  const sha = await getBlobSHA(fileObj);
  await client.uploadResource({
    sha,
    size
  }, fileObj);
  const pointerFileString = createPointerFile({
    sha,
    size
  });
  const pointerFileBlob = new Blob([pointerFileString]);
  const pointerFile = new File([pointerFileBlob], name, {
    type: 'text/plain'
  });
  const pointerFileSHA = await getBlobSHA(pointerFile);
  return {
    fileObj: pointerFile,
    size: pointerFileBlob.size,
    sha: pointerFileSHA,
    raw: pointerFileString,
    path
  };
}
export async function getLargeMediaFilteredMediaFiles(client, mediaFiles) {
  return await Promise.all(mediaFiles.map(async mediaFile => {
    const {
      fileObj,
      path
    } = mediaFile;
    const fixedPath = path.startsWith('/') ? path.slice(1) : path;
    if (!client.matchPath(fixedPath)) {
      return mediaFile;
    }
    const pointerFileDetails = await getPointerFileForMediaFileObj(client, fileObj, path);
    return {
      ...mediaFile,
      ...pointerFileDetails
    };
  }));
}